import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Sparkles, X } from 'lucide-react';
import { customAuthService } from '../services/customAuthService';
import { useTranslation } from '../contexts/TranslationContext';
import { submitDemoRequest } from '../services/demoRequestPublicService';
import Modal from '../components/Modal';

interface DemoFormState {
  full_name: string;
  email: string;
  phone: string;
  company_name: string;
  kvkk_onay: boolean;
  honeypot: string;
}

const initialDemoForm: DemoFormState = {
  full_name: '',
  email: '',
  phone: '',
  company_name: '',
  kvkk_onay: false,
  honeypot: '',
};

const formatPhoneInput = (value: string) => {
  const digits = value.replace(/\D/g, '').slice(0, 10);

  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  if (digits.length <= 8) return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)} ${digits.slice(6)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)} ${digits.slice(6, 8)} ${digits.slice(8, 10)}`;
};

const getPhoneDigits = (value: string) => value.replace(/\D/g, '');

const LoginPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rememberMe, setRememberMe] = useState(false);
  const [showDemoModal, setShowDemoModal] = useState(false);
  const [demoForm, setDemoForm] = useState<DemoFormState>(initialDemoForm);
  const [demoErrors, setDemoErrors] = useState<Partial<Record<keyof DemoFormState, string>>>({});
  const [demoSubmitting, setDemoSubmitting] = useState(false);
  const [demoSubmitted, setDemoSubmitted] = useState(false);
  const [feedbackModal, setFeedbackModal] = useState<{ title: string; message: string } | null>(null);

  useEffect(() => {
    const initializePage = async () => {
      customAuthService.logout();

      const remembered = localStorage.getItem('rememberMe');
      const rememberedEmail = localStorage.getItem('rememberedEmail');

      if (remembered === 'true' && rememberedEmail) {
        setEmail(rememberedEmail);
        setRememberMe(true);
      }
    };

    void initializePage();
  }, []);

  const handleForgotPassword = async () => {
    setFeedbackModal({
      title: 'Sifre Sifirlama',
      message: t('auth.contactAdminForPasswordReset'),
    });
  };

  const handleLogin = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!email || !password) {
      setError(t('auth.enterEmailAndPassword'));
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const result = await customAuthService.login(email, password);

      if (result.success && result.user) {
        if (rememberMe) {
          localStorage.setItem('rememberMe', 'true');
          localStorage.setItem('rememberedEmail', email);
        } else {
          localStorage.removeItem('rememberMe');
          localStorage.removeItem('rememberedEmail');
        }

        if (result.requiresPasswordChange) {
          setError(t('auth.tempPasswordWarning'));
          setTimeout(() => {
            navigate('/auth/update-password');
          }, 1200);
        } else {
          navigate('/anasayfa');
        }
      } else {
        localStorage.removeItem('custom_auth_token');
        localStorage.removeItem('custom_auth_user');
        localStorage.removeItem('persistent_session');
        setError(result.error || t('auth.loginFailed'));
      }
    } catch (err: unknown) {
      localStorage.removeItem('custom_auth_token');
      localStorage.removeItem('custom_auth_user');
      localStorage.removeItem('persistent_session');
      setError(t('auth.loginFailed') + ': ' + ((err as Error)?.message || t('common.unknownError')));
    } finally {
      setLoading(false);
    }
  };

  const closeDemoModal = () => {
    setShowDemoModal(false);
    setDemoForm(initialDemoForm);
    setDemoErrors({});
    setDemoSubmitting(false);
    setDemoSubmitted(false);
  };

  const validateDemoForm = () => {
    const nextErrors: Partial<Record<keyof DemoFormState, string>> = {};

    if (demoForm.full_name.trim().length < 2) nextErrors.full_name = 'Ad soyad zorunludur.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(demoForm.email.trim())) nextErrors.email = 'Gecerli bir e-posta girin.';
    if (getPhoneDigits(demoForm.phone).length !== 10) nextErrors.phone = 'Telefon numarasi 10 haneli olmalidir.';
    if (!demoForm.kvkk_onay) nextErrors.kvkk_onay = 'KVKK onayi zorunludur.';

    setDemoErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handlePhoneChange = (value: string) => {
    const formattedPhone = formatPhoneInput(value);
    setDemoForm((prev) => ({ ...prev, phone: formattedPhone }));
    if (demoErrors.phone) {
      setDemoErrors((prev) => ({ ...prev, phone: undefined }));
    }
  };

  const handleDemoSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!validateDemoForm()) return;

    try {
      setDemoSubmitting(true);
      const response = await submitDemoRequest({
        full_name: demoForm.full_name,
        email: demoForm.email,
        phone: getPhoneDigits(demoForm.phone),
        company_name: demoForm.company_name || undefined,
        source: 'login',
        kvkk_onay: demoForm.kvkk_onay,
        notify_ops: true,
        honeypot: demoForm.honeypot,
      });

      if (response.duplicate) {
        setFeedbackModal({
          title: 'Demo Talebi',
          message: response.message || 'Bu e-posta icin acik bir demo talebi zaten bulunuyor.',
        });
      } else {
        setFeedbackModal({
          title: 'Demo Talebi Alindi',
          message: 'Demo talebiniz alindi. Ekibimiz sizinle en kisa surede iletisime gececek.',
        });
      }

      setDemoSubmitted(true);
    } catch (err) {
      console.error('[LoginPage] Demo request submit failed', {
        error: err,
        email: demoForm.email,
        phoneDigits: getPhoneDigits(demoForm.phone),
      });
      setFeedbackModal({
        title: 'Demo Talebi Gonderilemedi',
        message: (err as Error)?.message || 'Demo talebi gonderilemedi.',
      });
    } finally {
      setDemoSubmitting(false);
    }
  };

  return (
    <>
      <h2 className="text-center text-2xl font-bold text-gray-900 mb-6">{t('auth.login')}</h2>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 border px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      <form className="space-y-6" onSubmit={handleLogin}>
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700">
            {t('auth.email')}
          </label>
          <div className="mt-1">
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="appearance-none block w-full px-3 py-2 border-2 border-black rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              style={{ border: '2px solid #000000', borderRadius: '6px', color: '#000000' }}
            />
          </div>
        </div>

        <div>
          <label htmlFor="password" className="block text-sm font-medium text-gray-700">
            {t('auth.password')}
          </label>
          <div className="mt-1 relative">
            <input
              id="password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="appearance-none block w-full px-3 py-2 pr-10 border-2 border-black rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              style={{ border: '2px solid #000000', borderRadius: '6px', color: '#000000' }}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div
              onClick={() => setRememberMe(!rememberMe)}
              style={{
                width: '18px',
                height: '18px',
                borderRadius: '3px',
                border: '2px solid #3B82F6',
                backgroundColor: rememberMe ? '#3B82F6' : 'transparent',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
            >
              {rememberMe && (
                <span style={{ color: 'white', fontSize: '12px', fontWeight: 'bold' }}>✓</span>
              )}
            </div>
            <label
              htmlFor="remember-me"
              className="ml-2 block text-sm text-gray-900 cursor-pointer"
              onClick={() => setRememberMe(!rememberMe)}
            >
              {t('auth.rememberMe')}
            </label>
          </div>

          <div className="text-sm">
            <button
              type="button"
              onClick={handleForgotPassword}
              className="font-medium text-blue-600 hover:text-blue-500"
            >
              {t('auth.forgotPassword')}
            </button>
          </div>
        </div>

        <div>
          <button
            type="submit"
            disabled={loading}
            className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
              loading ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
            }`}
          >
            {loading ? t('auth.loggingIn') : t('auth.login')}
          </button>
        </div>

        <div>
          <button
            type="button"
            onClick={() => setShowDemoModal(true)}
            className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-md border border-blue-200 bg-blue-50 text-blue-700 font-semibold hover:bg-blue-100"
          >
            <Sparkles size={16} />
            Demo Talep Et
          </button>
        </div>
      </form>

      <div className="mt-6 text-center text-sm text-gray-500">
        <p>{t('auth.noAccount')}</p>
        <div className="mt-4 pt-4 border-t border-gray-200">
          <p className="mb-2">{t('auth.acceptTerms')}</p>
          <div className="flex justify-center space-x-4">
            <Link to="/auth/kvkk-aydinlatma" className="text-blue-600 hover:underline">KVKK Aydınlatma Metni</Link>
            <span className="text-gray-400">|</span>
            <Link to="/auth/kvkk-acik-riza" className="text-blue-600 hover:underline">Açık Rıza Metni</Link>
          </div>
        </div>
      </div>

      {showDemoModal && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(15, 23, 42, 0.55)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '1rem',
          zIndex: 1000,
        }}>
          <div style={{ background: 'white', borderRadius: '14px', width: '100%', maxWidth: '560px', padding: '1.5rem', position: 'relative', boxShadow: '0 18px 42px rgba(15, 23, 42, 0.2)' }}>
            <button
              type="button"
              onClick={closeDemoModal}
              style={{ position: 'absolute', right: '12px', top: '12px', border: 'none', background: 'transparent', cursor: 'pointer', color: '#64748b' }}
            >
              <X size={18} />
            </button>

            {!demoSubmitted ? (
              <>
                <h3 style={{ margin: 0, fontSize: '1.3rem' }}>Demo Talep Formu</h3>
                <p style={{ color: '#64748b', margin: '0.5rem 0 1.25rem' }}>Bilgilerinizi birakin, demo kullanicisi olusturalim.</p>

                <form onSubmit={handleDemoSubmit} style={{ display: 'grid', gap: '1rem' }}>
                  <div style={{ display: 'none' }}>
                    <input value={demoForm.honeypot} onChange={(e) => setDemoForm((prev) => ({ ...prev, honeypot: e.target.value }))} />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '0.35rem', fontWeight: 600 }}>Ad Soyad *</label>
                    <input
                      value={demoForm.full_name}
                      onChange={(e) => setDemoForm((prev) => ({ ...prev, full_name: e.target.value }))}
                      style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', color: '#111827' }}
                    />
                    {demoErrors.full_name && <p style={{ color: '#dc2626', fontSize: '0.8rem', marginTop: '0.35rem' }}>{demoErrors.full_name}</p>}
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.35rem', fontWeight: 600 }}>E-posta *</label>
                      <input
                        value={demoForm.email}
                        onChange={(e) => setDemoForm((prev) => ({ ...prev, email: e.target.value }))}
                        style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', color: '#111827' }}
                      />
                      {demoErrors.email && <p style={{ color: '#dc2626', fontSize: '0.8rem', marginTop: '0.35rem' }}>{demoErrors.email}</p>}
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: '0.35rem', fontWeight: 600 }}>Telefon *</label>
                      <input
                        value={demoForm.phone}
                        onChange={(e) => handlePhoneChange(e.target.value)}
                        maxLength={16}
                        placeholder="(555) 555 55 55"
                        style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', color: '#111827' }}
                      />
                      {demoErrors.phone && <p style={{ color: '#dc2626', fontSize: '0.8rem', marginTop: '0.35rem' }}>{demoErrors.phone}</p>}
                    </div>
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '0.35rem', fontWeight: 600 }}>Firma Adı</label>
                    <input
                      value={demoForm.company_name}
                      onChange={(e) => setDemoForm((prev) => ({ ...prev, company_name: e.target.value }))}
                      style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', color: '#111827' }}
                    />
                  </div>

                  <label style={{ display: 'flex', gap: '0.6rem', alignItems: 'flex-start', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '0.9rem', background: '#f8fafc', color: '#334155', fontSize: '0.92rem' }}>
                    <input
                      type="checkbox"
                      checked={demoForm.kvkk_onay}
                      onChange={(e) => setDemoForm((prev) => ({ ...prev, kvkk_onay: e.target.checked }))}
                    />
                    <span>KVKK metnini okudum, demo sureci icin bilgilerimin islenmesini kabul ediyorum. *</span>
                  </label>
                  {demoErrors.kvkk_onay && <p style={{ color: '#dc2626', fontSize: '0.8rem', marginTop: '-0.4rem' }}>{demoErrors.kvkk_onay}</p>}

                  <button
                    type="submit"
                    disabled={demoSubmitting}
                    style={{ padding: '12px 14px', borderRadius: '8px', border: 'none', background: demoSubmitting ? '#93c5fd' : '#2563eb', color: 'white', cursor: demoSubmitting ? 'not-allowed' : 'pointer', fontWeight: 700 }}
                  >
                    {demoSubmitting ? 'Gonderiliyor...' : 'Demo Talebi Gonder'}
                  </button>
                </form>
              </>
            ) : (
              <div style={{ textAlign: 'center', padding: '1rem 0 0.5rem' }}>
                <h3 style={{ margin: 0 }}>Talebiniz Alindi</h3>
                <p style={{ color: '#64748b', marginTop: '0.75rem' }}>Demo kullanicisi icin ekibimiz sizinle en kisa surede iletisime gececek.</p>
                <button
                  type="button"
                  onClick={closeDemoModal}
                  style={{ marginTop: '1rem', padding: '10px 14px', borderRadius: '8px', border: 'none', background: '#2563eb', color: 'white', cursor: 'pointer' }}
                >
                  Kapat
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      <Modal
        isOpen={Boolean(feedbackModal)}
        onClose={() => setFeedbackModal(null)}
        title={feedbackModal?.title || 'Bilgilendirme'}
        size="sm"
      >
        <div style={{ display: 'grid', gap: '1rem' }}>
          <p style={{ margin: 0, color: '#475569', lineHeight: 1.6 }}>
            {feedbackModal?.message}
          </p>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={() => setFeedbackModal(null)}
              style={{ padding: '10px 14px', borderRadius: '8px', border: 'none', background: '#2563eb', color: 'white', cursor: 'pointer', fontWeight: 600 }}
            >
              Tamam
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default LoginPage;
